import { EventListenerFields, InstrumentationEventAddEventListenerCalled, MonitoredNavigatorProperty } from "../Events/InstrumentationEvent";

export const PointerProperties = ['clientX', 'clientY', 'offsetX', 'offsetY', 'pageX', 'pageY', 'screenX', 'screenY'] as const;
export type PointerProperty = typeof PointerProperties[number];
const PointerPropertySet = new Set(PointerProperties);
export const isPointerProperty = (candidate: unknown): candidate is PointerProperty =>
	typeof candidate === "string" && PointerPropertySet.has(candidate.toLocaleLowerCase() as PointerProperty);

export type GeolocationMethod = 'getCurrentPosition' | 'watchPosition';

export const GlobalTags = [
	'window',
	'document',
	'body',
	'html',
] as const;
export type GlobalTag = NonNullable<typeof GlobalTags[number]>;
const GlobalTagSet = new Set(GlobalTags);
export const isGlobalTag = (candidate: unknown): candidate is GlobalTag =>
	typeof candidate === "string" && GlobalTagSet.has(candidate.toLocaleLowerCase() as GlobalTag);

export const KeyEvents = ['keydown', 'keyup', 'keypress'] as const satisfies (keyof WindowEventMap)[];
export type KeyEvent = NonNullable<typeof KeyEvents[number]>;
const KeyEventSet = new Set(KeyEvents);
export const isKeyEvent = (candidate: unknown): candidate is KeyEvent => KeyEventSet.has(candidate as KeyEvent);

export const InputElementEvents = ['input', 'change', 'paste'] as const satisfies (keyof WindowEventMap)[];;
export type InputElementEvent = NonNullable<typeof InputElementEvents[number]>;
const InputElementEventSet = new Set(InputElementEvents);
export const isInputElementEvent = (candidate: unknown): candidate is InputElementEvent =>
	typeof candidate === "string" && InputElementEventSet.has(candidate.toLocaleLowerCase() as InputElementEvent);

export const MouseEvents = ['click', 'mousedown', 'mouseup', 'mousemove',
	'pointermove', 'pointerup', 'pointerdown', 'pointerrawupdate' as 'pointermove'] as const satisfies (keyof WindowEventMap)[];;
export type MouseEvent = NonNullable<typeof MouseEvents[number]>;
const MouseEventSet = new Set(MouseEvents);
export const isMouseEvent = (candidate: unknown): candidate is MouseEvent =>
	typeof candidate === "string" && MouseEventSet.has(candidate.toLocaleLowerCase() as MouseEvent);

export const OtherInterestingEvents = ['devicemotion', 'deviceorientation'] as const;

export type ReportableEvent = KeyEvent | InputElementEvent | MouseEvent;
export const isReportableEvent = (candidate: unknown) =>
	isKeyEvent(candidate) ||
	isInputElementEvent(candidate) ||
	isMouseEvent(candidate);

export const isReportableEventAddEventListener = (candidate: InstrumentationEventAddEventListenerCalled["raw"]): candidate is typeof candidate & EventListenerFields<ReportableEvent> =>
	isReportableEvent(candidate.domOrCustomEventName);


export type MonitoredAPIConstant = `Navigator.${MonitoredNavigatorProperty}`;

export const CodeBehaviorTypeNameGlobalListener = "GlobalListener" as const;
export const CodeBehaviorTypeNameApiConstantsRead = "ApiConstant" as const;
export const CodeBehaviorTypeNameHasCalled = "HasCalled" as const;
export const CodeBehaviorTypeNameGlobalPropertiesRead = "GlobalPropertyRead" as const;
export const CodeBehaviorTypeNameKeystrokeRead = "KeystrokeRead" as const;
export const CodeBehaviorTypeNameGeolocationRead = "GeolocationRead" as const;
export const CodeBehaviorTypeNameCanvasRenderingContext = "CanvasRenderingContext" as const;

export type CodeBehaviorTypeName =
	typeof CodeBehaviorTypeNameGlobalListener |
	typeof CodeBehaviorTypeNameApiConstantsRead |
	typeof CodeBehaviorTypeNameHasCalled |
	typeof CodeBehaviorTypeNameGlobalPropertiesRead |
	typeof CodeBehaviorTypeNameKeystrokeRead |
	typeof CodeBehaviorTypeNameGeolocationRead |
	typeof CodeBehaviorTypeNameCanvasRenderingContext |
	never;

export const BehaviorTypePrefix = <T extends CodeBehaviorTypeName = CodeBehaviorTypeName>(CodeBehaviorTypeName: T) =>
	`${CodeBehaviorTypeName}\t` as const;
export type BehaviorTypePrefix = ReturnType<typeof BehaviorTypePrefix<CodeBehaviorTypeName>>;
export const BehaviorTypePrefixedString = <T extends CodeBehaviorTypeName, U extends string>(CodeBehaviorTypeName: T, s: U) => `${CodeBehaviorTypeName}\t${s}` as const;

export const CodeBehaviorTypeNameGlobalListenerPrefix = BehaviorTypePrefix(CodeBehaviorTypeNameGlobalListener);
export const CodeBehaviorTypeNameApiConstantsReadPrefix = BehaviorTypePrefix(CodeBehaviorTypeNameApiConstantsRead);
export const CodeBehaviorTypeNameHasCalledPrefix = BehaviorTypePrefix(CodeBehaviorTypeNameHasCalled);
export const CodeBehaviorTypeNameGlobalPropertiesReadPrefix = BehaviorTypePrefix(CodeBehaviorTypeNameGlobalPropertiesRead);
export const CodeBehaviorTypeNameKeystrokeReadPrefix = BehaviorTypePrefix(CodeBehaviorTypeNameKeystrokeRead);
export const CodeBehaviorTypeNameGeolocationReadPrefix = BehaviorTypePrefix(CodeBehaviorTypeNameGeolocationRead);
export const CodeBehaviorTypeNameCanvasRenderingContextPrefix = BehaviorTypePrefix(CodeBehaviorTypeNameCanvasRenderingContext);

export type CodeBehaviorTypeNameGlobalListener = typeof CodeBehaviorTypeNameGlobalListener;
export interface CodeBehaviorGlobalListener {
	domEventName: string;
	tag: GlobalTag;
}
export const CodeBehaviorGlobalListenerString = ({ domEventName, tag }: CodeBehaviorGlobalListener) => 
	BehaviorTypePrefixedString(CodeBehaviorTypeNameGlobalListener, `${domEventName}\t${tag}` as const);
export type CodeBehaviorGlobalListenerString = ReturnType<typeof CodeBehaviorGlobalListenerString>;
export const isCodeBehaviorGlobalListener = (candidate: CodeBehaviorString): candidate is CodeBehaviorGlobalListenerString => candidate.startsWith(CodeBehaviorTypeNameGlobalListenerPrefix);
export const isCodeBehaviorGlobalListenerEntry = (candidate: [CodeBehaviorString, unknown]): candidate is [CodeBehaviorGlobalListenerString, number] => candidate[0].startsWith(CodeBehaviorTypeNameGlobalListenerPrefix);

export const CodeBehaviorGlobalListener = (fromString: CodeBehaviorGlobalListenerString): CodeBehaviorGlobalListener => {
	const [_, domEventName, tag] = fromString.split("\t") as [string, string, GlobalTag];
	return { domEventName, tag };
};

export interface CodeBehaviorApiConstantsRead {
	apiConstant: MonitoredAPIConstant;
	value: string | number;
}
export const CodeBehaviorApiConstantsReadString = ({ apiConstant, value }: CodeBehaviorApiConstantsRead) =>
	BehaviorTypePrefixedString(CodeBehaviorTypeNameApiConstantsRead,`${apiConstant}\t${value}` as const);
export type CodeBehaviorApiConstantsReadString = ReturnType<typeof CodeBehaviorApiConstantsReadString>;
export const isCodeBehaviorApiConstantsRead = (candidate: CodeBehaviorString): candidate is CodeBehaviorApiConstantsReadString => candidate.startsWith(CodeBehaviorTypeNameApiConstantsReadPrefix);
export const isCodeBehaviorApiConstantsReadEntry = (candidate: [CodeBehaviorString, unknown]): candidate is [CodeBehaviorApiConstantsReadString, number] => candidate[0].startsWith(CodeBehaviorTypeNameApiConstantsReadPrefix);
export const CodeBehaviorApiConstantsRead = (fromString: CodeBehaviorApiConstantsReadString): CodeBehaviorApiConstantsRead => {
	const [_, apiConstant, value = ""] = fromString.split("\t") as [string, MonitoredAPIConstant, string];
	return { apiConstant, value };
};

export interface CodeBehaviorHasCalled {
	method: GeolocationMethod;
}
export const CodeBehaviorHasCalledString = ({ method }: CodeBehaviorHasCalled) =>
	`HasCalled\t${method}` as const;
export type CodeBehaviorHasCalledString = ReturnType<typeof CodeBehaviorHasCalledString>;
export const isCodeBehaviorHasCalled = (candidate: CodeBehaviorString): candidate is CodeBehaviorHasCalledString => candidate.startsWith(CodeBehaviorTypeNameHasCalledPrefix);
export const isCodeBehaviorHasCalledEntry = (candidate: [CodeBehaviorString, unknown]): candidate is [CodeBehaviorHasCalledString, number] => candidate[0].startsWith(CodeBehaviorTypeNameHasCalledPrefix);
export const CodeBehaviorHasCalled = (fromString: CodeBehaviorHasCalledString): CodeBehaviorHasCalled => {
	const [_, method] = fromString.split("\t") as [string, GeolocationMethod];
	return { method };
};

export interface CodeBehaviorGlobalPropertiesRead {
	domEventName: string;
	tag: GlobalTag;
	property: PointerProperty;
}
export const CodeBehaviorGlobalPropertiesReadString = ({ tag, domEventName, property }: CodeBehaviorGlobalPropertiesRead) =>
	BehaviorTypePrefixedString(CodeBehaviorTypeNameGlobalPropertiesRead,`${tag}\t${domEventName}\t${property}` as const);
export type CodeBehaviorGlobalPropertiesReadString = ReturnType<typeof CodeBehaviorGlobalPropertiesReadString>;
export const isCodeBehaviorGlobalPropertiesRead = (candidate: CodeBehaviorString): candidate is CodeBehaviorGlobalPropertiesReadString => candidate.startsWith(CodeBehaviorTypeNameGlobalPropertiesReadPrefix);
export const isCodeBehaviorGlobalPropertiesReadEntry = (candidate: [CodeBehaviorString, unknown]): candidate is [CodeBehaviorGlobalPropertiesReadString, number] => candidate[0].startsWith(CodeBehaviorTypeNameGlobalPropertiesReadPrefix);
export const CodeBehaviorGlobalPropertiesRead = (fromString: CodeBehaviorGlobalPropertiesReadString): CodeBehaviorGlobalPropertiesRead => {
	const [_, tag,domEventName, property] = fromString.split("\t") as [string, GlobalTag, string, PointerProperty];
	return { tag, domEventName, property };
};

export interface CodeBehaviorGeolocationRead {
	latitude: number,
	longitude: number,
}
export const CodeBehaviorStringGeolocationRead = ({ latitude, longitude }: CodeBehaviorGeolocationRead) =>
	BehaviorTypePrefixedString(CodeBehaviorTypeNameGeolocationRead,`${latitude}\t${longitude}` as const);
export type CodeBehaviorGeolocationReadString = ReturnType<typeof CodeBehaviorStringGeolocationRead>;
export const isCodeBehaviorGeolocationRead = (candidate: CodeBehaviorString): candidate is CodeBehaviorGeolocationReadString =>
	candidate.startsWith(CodeBehaviorTypeNameGeolocationReadPrefix);
export const isCodeBehaviorGeolocationReadEntry = (candidate: [CodeBehaviorString, unknown]): candidate is [CodeBehaviorGeolocationReadString, number] =>
	candidate[0].startsWith(CodeBehaviorTypeNameGeolocationReadPrefix);
export const CodeBehaviorGeolocationRead = (fromString: CodeBehaviorGeolocationReadString): CodeBehaviorGeolocationRead => {
	const [_, latitudeString, longitudeString] = fromString.split("\t") as [string, string, string];
	const latitude = parseFloat(latitudeString);
	const longitude = parseFloat(longitudeString);
	return { latitude, longitude };
};


export interface CodeBehaviorCanvasRenderingContext {
	contextId: string;
}
export const CodeBehaviorStringCanvasRenderingContextString = ({ contextId }: CodeBehaviorCanvasRenderingContext) =>
	BehaviorTypePrefixedString(CodeBehaviorTypeNameCanvasRenderingContext,`${contextId}` as const);
export type CodeBehaviorCanvasRenderingContextString = ReturnType<typeof CodeBehaviorStringCanvasRenderingContextString>;
export const isCodeBehaviorCanvasRenderingContext = (candidate: CodeBehaviorString): candidate is CodeBehaviorCanvasRenderingContextString =>
	candidate.startsWith(CodeBehaviorTypeNameCanvasRenderingContextPrefix);
export const isCodeBehaviorCanvasRenderingContextEntry = (candidate: [CodeBehaviorString, unknown]): candidate is [CodeBehaviorCanvasRenderingContextString, number] =>
	candidate[0].startsWith(CodeBehaviorTypeNameCanvasRenderingContextPrefix);
export const CodeBehaviorCanvasRenderingContext = (fromString: CodeBehaviorCanvasRenderingContextString): CodeBehaviorCanvasRenderingContext => {
	const [_, contextId ] = fromString.split("\t") as [string, string];
	return { contextId };
};


export interface CodeBehaviorKeystrokeRead {
	keyEvent: KeyEvent;
	tag: string;
}
export const CodeBehaviorKeystrokeReadString = ({ tag, keyEvent }: CodeBehaviorKeystrokeRead) =>
	BehaviorTypePrefixedString(CodeBehaviorTypeNameKeystrokeRead ,`${tag}\t${keyEvent}` as const);
export type CodeBehaviorStringKeystrokeRead = ReturnType<typeof CodeBehaviorKeystrokeReadString>;
export const isCodeBehaviorKeystrokeRead = (candidate: CodeBehaviorString): candidate is CodeBehaviorStringKeystrokeRead =>
	candidate.startsWith(CodeBehaviorTypeNameKeystrokeReadPrefix);
export const isCodeBehaviorKeystrokeReadEntry = (candidate: [CodeBehaviorString, unknown]): candidate is [CodeBehaviorStringKeystrokeRead, string] =>
	candidate[0].startsWith(CodeBehaviorTypeNameKeystrokeReadPrefix);
export const CodeBehaviorKeystrokeRead = (fromString: CodeBehaviorStringKeystrokeRead): CodeBehaviorKeystrokeRead => {
	const [_, tag, keyEvent] = fromString.split("\t") as [string, string, KeyEvent];
	return { tag, keyEvent };
};


export type BehaviorsObservedStringFirstIndex =
	CodeBehaviorGlobalListenerString |
	CodeBehaviorApiConstantsReadString |
	CodeBehaviorHasCalledString |
	CodeBehaviorGlobalPropertiesReadString |
	CodeBehaviorCanvasRenderingContextString |
	CodeBehaviorGeolocationReadString |
	never;

export type BehaviorsObservedMultipleIndexes =
	never;


export type CodeBehaviorString =
	BehaviorsObservedStringFirstIndex |
	CodeBehaviorStringKeystrokeRead |
	never;

// const CodePrefix = "code:" as const;
// export type CodePrefix = typeof CodePrefix;
// export type CodeBehaviorAtCodeUrlString<T extends CodeBehaviorString = CodeBehaviorString> = `${CodePrefix}${string}\t${T}`;
// export const BehaviorsObservedAtCodeUrlString = <T extends CodeBehaviorString = CodeBehaviorString>(codeLocationParameterlessUrl: string, CodeBehaviorString: T) =>
// 	`${CodePrefix}${codeLocationParameterlessUrl}\t${CodeBehaviorString}` as const satisfies CodeBehaviorAtCodeUrlString;

// export const StripCodeUrlFromBehaviorsObservedAtCodeUrlString = <T extends CodeBehaviorString = CodeBehaviorString>(
// 	CodeBehaviorAtCodeUrlString: CodeBehaviorAtCodeUrlString<T>
// ) => {
// 	const [codePrefixAndUrl, CodeBehaviorString] = CodeBehaviorAtCodeUrlString.split("\t") as [string, T];
// 	const codeUrl = codePrefixAndUrl.slice(CodePrefix.length);
// 	return {codeUrl, CodeBehaviorString} as const;
// }
//export type CodeBehaviorAtCodeUrlString = ReturnType<typeof BehaviorsObservedAtCodeUrlString>;

export type CodeBehaviorsObservedCompactUpdate =
	[BehaviorsObservedStringFirstIndex, number] |
	[CodeBehaviorStringKeystrokeRead, string];

export type CodeBehaviorsObservedCompact = {
	[key in BehaviorsObservedStringFirstIndex]: number;
} & {
	[key in CodeBehaviorStringKeystrokeRead]: string;
};

// export type BehaviorsObservedCompact =
// 	Record<CodeBehaviorAtCodeUrlString<BehaviorsObservedStringFirstIndex>, number> |
// 	Record<CodeBehaviorAtCodeUrlString<CodeBehaviorStringKeystrokeRead>, string>;

// export type BehaviorsObservedWithCodeUrlUpdate =
// 	[CodeBehaviorAtCodeUrlString<BehaviorsObservedStringFirstIndex>, number] |
// 	[CodeBehaviorAtCodeUrlString<CodeBehaviorStringKeystrokeRead>, string];


// export type BehaviorsObservedWithCodeUrlCompact = {
// 	[key: CodeBehaviorAtCodeUrlString<BehaviorsObservedStringFirstIndex>]: number;
// 	[key: CodeBehaviorAtCodeUrlString<CodeBehaviorStringKeystrokeRead>]: string;
// };

type EventIndex = number;
export interface CodeBehaviorsObserved {
	globalListeners: (CodeBehaviorGlobalListener & {eventIndex: EventIndex})[];
	apiConstants: (CodeBehaviorApiConstantsRead & {eventIndex: EventIndex})[];
	hasCalled: (CodeBehaviorHasCalled & {eventIndex: EventIndex})[];
	globalPropertiesRead: (CodeBehaviorGlobalPropertiesRead & {eventIndex: EventIndex})[];
	geolocationsRead: (CodeBehaviorGeolocationRead & {eventIndex: EventIndex})[];
	canvasRenderingContexts: (CodeBehaviorCanvasRenderingContext & {eventIndex: EventIndex})[];

	keystrokesRead: (CodeBehaviorKeystrokeRead & {keystrokesAsString: string})[];
}

export const CodeBehaviorsObserved = (fromCompactEntries?: CodeBehaviorsObservedCompact | CodeBehaviorsObservedCompactUpdate[]) => {
	const behaviorsObserved: CodeBehaviorsObserved = {
		apiConstants: [],
		globalListeners: [],
		hasCalled: [],
		globalPropertiesRead: [],
		geolocationsRead: [],
		keystrokesRead: [],
		canvasRenderingContexts: [],
	};
	if (fromCompactEntries != null) {
		updateCodeBehaviorsObserved(behaviorsObserved, fromCompactEntries ?? {});
	}
	return behaviorsObserved;
}

export const updateCodeBehaviorsObserved = (behaviorsObserved: CodeBehaviorsObserved, fromCompactEntries: CodeBehaviorsObservedCompact | CodeBehaviorsObservedCompactUpdate[]) => {
	const entries = Array.isArray(fromCompactEntries) ? fromCompactEntries : Object.entries(fromCompactEntries) as CodeBehaviorsObservedCompactUpdate[];
	for (const entry of entries) {
		if (isCodeBehaviorGlobalListenerEntry(entry)) {
			const [fromString, eventIndex] = entry;
			behaviorsObserved.globalListeners.push({eventIndex, ...CodeBehaviorGlobalListener(fromString)});
		} else if (isCodeBehaviorApiConstantsReadEntry(entry)) {
			const [fromString, eventIndex] = entry;
			behaviorsObserved.apiConstants.push({eventIndex, ...CodeBehaviorApiConstantsRead(fromString)});
		} else if (isCodeBehaviorHasCalledEntry(entry)) {
			const [fromString, eventIndex] = entry;
			behaviorsObserved.hasCalled.push({eventIndex, ...CodeBehaviorHasCalled(fromString)});
		} else if (isCodeBehaviorGlobalPropertiesReadEntry(entry)) {
			const [fromString, eventIndex] = entry;
			behaviorsObserved.globalPropertiesRead.push({eventIndex, ...CodeBehaviorGlobalPropertiesRead(fromString)});
		} else if (isCodeBehaviorGeolocationReadEntry(entry)) {
			const [fromString, eventIndex] = entry;
			behaviorsObserved.geolocationsRead.push({eventIndex, ...CodeBehaviorGeolocationRead(fromString)});
		} else if (isCodeBehaviorCanvasRenderingContextEntry(entry)) {
			const [fromString, eventIndex] = entry;
			behaviorsObserved.canvasRenderingContexts.push({eventIndex, ...CodeBehaviorCanvasRenderingContext(fromString)});
		} else if (isCodeBehaviorKeystrokeReadEntry(entry)) {
			const [fromString, keystrokesAsString] = entry;
			behaviorsObserved.keystrokesRead.push({keystrokesAsString, ...CodeBehaviorKeystrokeRead(fromString)});
		}
	}
	return behaviorsObserved;
}

